import { afterNextRender, ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, output, signal } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { SharedModule } from '@aiwyn/shared';

/** One item row in the invite tree, with the dialog's disable rules already applied. */
export interface CrlInviteTreeRow {
  id: number;
  name: string;
  /** `A.1`-style display number. */
  displayNumber: string;
  /** True when the row cannot be ticked — nothing to grant, or the address already has access. */
  isDisabled: boolean;
  /** Muted hint shown beside a disabled row; null when the row is selectable. */
  disabledReason: string | null;
}

/** One section of the invite tree. */
export interface CrlInviteTreeRowSection {
  id: number;
  name: string;
  /** Section letter (`A`, `B`, …). */
  identifier: string;
  rows: CrlInviteTreeRow[];
}

interface CrlInviteTreeRowView extends CrlInviteTreeRow {
  isChecked: boolean;
  /**
   * Id of the row's visually-hidden reason text, referenced by the checkbox's
   * `aria-describedby`. Null while the row is selectable, so assistive tech is
   * told why a row is unavailable without the reason being on screen.
   */
  reasonId: string | null;
}

interface CrlInviteTreeSectionView {
  id: number;
  name: string;
  identifier: string;
  rows: CrlInviteTreeRowView[];
  isExpanded: boolean;
  /** Ticked when every selectable row in the section is ticked. */
  isChecked: boolean;
  isIndeterminate: boolean;
  /** A section with nothing selectable cannot be ticked as a whole. */
  isDisabled: boolean;
}

/**
 * Collapsible section/item checkbox tree for the CRL Invite dialog: one invite
 * can grant access to several items of the request.
 *
 * Presentational — the caller passes rows that already carry their disabled
 * state and owns the resulting selection; this component only maps clicks onto
 * the next set of selected ids. A section checkbox covers the rows it can
 * actually act on: it reads ticked when every SELECTABLE row is ticked,
 * indeterminate while only some are, and ticking it never drags a disabled row
 * into the selection.
 *
 * A disabled row explains itself on hover through a tooltip on the row wrapper —
 * the shared tooltip directive listens for mouse events, which a disabled
 * checkbox never emits — and to assistive tech through a visually-hidden reason
 * the checkbox points at with `aria-describedby`.
 */
@Component({
  selector: 'cxp-crl-invite-item-tree',
  templateUrl: './crl-invite-item-tree.component.html',
  styleUrls: ['./crl-invite-item-tree.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SharedModule, MatCheckboxModule],
})
export class CrlInviteItemTreeComponent {
  private readonly hostRef = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly sections = input.required<CrlInviteTreeRowSection[]>();
  /** Ids currently selected; rows not listed here render unticked. */
  readonly selectedItemIds = input<readonly number[]>([]);
  /**
   * Row to bring into view on first render — the item the dialog was opened
   * from, which on a long request sits well below the fold.
   */
  readonly scrollToItemId = input<number | null>(null);

  /** The full set of selected ids after a tick — the caller replaces its selection with it. */
  readonly selectionChange = output<number[]>();

  /** Sections start expanded so the item the dialog pre-ticked is visible without a click. */
  private readonly collapsedSectionIds = signal<ReadonlySet<number>>(new Set());

  protected readonly viewSections = computed<CrlInviteTreeSectionView[]>(() => {
    const selected = new Set(this.selectedItemIds());
    const collapsed = this.collapsedSectionIds();

    return this.sections().map((section) => {
      const rows = section.rows.map((row) => ({
        ...row,
        isChecked: !row.isDisabled && selected.has(row.id),
        reasonId: row.isDisabled && row.disabledReason ? `cxp-crl-invite-tree-reason-${row.id}` : null,
      }));
      const selectableRows = rows.filter((row) => !row.isDisabled);
      const checkedCount = selectableRows.filter((row) => row.isChecked).length;

      return {
        id: section.id,
        name: section.name,
        identifier: section.identifier,
        rows,
        isExpanded: !collapsed.has(section.id),
        isChecked: selectableRows.length > 0 && checkedCount === selectableRows.length,
        isIndeterminate: checkedCount > 0 && checkedCount < selectableRows.length,
        isDisabled: selectableRows.length === 0,
      };
    });
  });

  constructor() {
    // Once, after the first render. Deliberately not an effect: the reveal is a
    // one-time courtesy, and re-running it would yank the list back every time
    // the email filter recomputes the rows or the user ticks a box.
    afterNextRender(() => this.revealOpeningRow());
  }

  /**
   * Scrolls THIS tree's own container so the opening item's row is in view, one
   * row's worth of space above it so the section it belongs to stays readable
   * rather than being cut off at the top edge. Sets `scrollTop` from the row's
   * offset rather than calling `scrollIntoView`, which walks every scrollable
   * ancestor and would drag the dialog around with it. A no-op when the row is
   * not rendered.
   */
  private revealOpeningRow(): void {
    const itemId = this.scrollToItemId();
    if (itemId === null) return;

    const container = this.hostRef.nativeElement;
    const row = container.querySelector<HTMLElement>(`[data-row-id="${itemId}"]`);
    if (!row) return;

    container.scrollTop = Math.max(0, row.offsetTop - row.offsetHeight);
  }

  /**
   * Whether there is anything left to expand / collapse. Together with
   * {@link expandAll} and {@link collapseAll} these are the control surface for
   * an Expand All / Collapse All affordance rendered OUTSIDE this component —
   * the tree's own box scrolls, so such a control cannot live in it. Both are
   * false for an empty tree, which has nothing to offer.
   */
  readonly canExpandAll = computed(() => this.viewSections().some((section) => !section.isExpanded));
  readonly canCollapseAll = computed(() => this.viewSections().some((section) => section.isExpanded));

  expandAll(): void {
    this.collapsedSectionIds.set(new Set());
  }

  collapseAll(): void {
    this.collapsedSectionIds.set(new Set(this.sections().map((section) => section.id)));
  }

  protected onSectionExpandToggled(sectionId: number): void {
    this.collapsedSectionIds.update((collapsed) => {
      const next = new Set(collapsed);
      if (!next.delete(sectionId)) {
        next.add(sectionId);
      }
      return next;
    });
  }

  /** Ticks or unticks every selectable row in the section. */
  protected onSectionToggled(section: CrlInviteTreeSectionView): void {
    const selected = new Set(this.selectedItemIds());
    for (const row of section.rows) {
      if (row.isDisabled) continue;
      if (section.isChecked) {
        selected.delete(row.id);
      } else {
        selected.add(row.id);
      }
    }
    this.selectionChange.emit([...selected]);
  }

  protected onRowToggled(row: CrlInviteTreeRowView): void {
    if (row.isDisabled) return;

    const selected = new Set(this.selectedItemIds());
    if (!selected.delete(row.id)) {
      selected.add(row.id);
    }
    this.selectionChange.emit([...selected]);
  }
}
